import React, { useCallback, useEffect, useRef, useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
import {
  FiAlertCircle,
  FiCheck,
  FiCode,
  FiCopy,
  FiLoader,
  FiPlay,
  FiRefreshCw,
  FiTerminal,
  FiTrash2,
} from "react-icons/fi";
import { DS } from "../utils/designSystem";

/* ── Constants ─────────────────────────────────────────────────── */

const PYODIDE_CDN = "https://cdn.jsdelivr.net/pyodide/v0.26.4/full/";

const STARTER_CODE = `# KodeVedic Python Playground
# Powered by Pyodide — runs entirely in your browser, no server needed.

def fibonacci(n):
    """Return the first n Fibonacci numbers."""
    a, b = 0, 1
    result = []
    for _ in range(n):
        result.append(a)
        a, b = b, a + b
    return result


nums = fibonacci(10)
print("Fibonacci sequence:", nums)
print("Sum:", sum(nums))

import math
print(f"\\nπ ≈ {math.pi:.6f}")
print(f"e ≈ {math.e:.6f}")
`;

/* ── Helpers ────────────────────────────────────────────────────── */

function StatusPill({ status }) {
  const cfg = {
    loading: { cls: "text-yellow-50",          label: "Loading runtime…", spin: true  },
    ready:   { cls: "text-caribbeangreen-100",  label: "Ready",            spin: false },
    running: { cls: "text-blue-200",            label: "Running…",         spin: true  },
    error:   { cls: "text-pink-200",            label: "Load failed",      spin: false },
  };
  const { cls, label, spin } = cfg[status] ?? cfg.loading;
  return (
    <span className={`flex items-center gap-1.5 text-[11px] font-medium ${cls}`}>
      {spin ? (
        <FiLoader size={12} className="animate-spin" />
      ) : status === "error" ? (
        <FiAlertCircle size={12} />
      ) : (
        <FiCheck size={12} />
      )}
      {label}
    </span>
  );
}

/* ── Main Component ─────────────────────────────────────────────── */

function PythonCompiler() {
  const [code, setCode]         = useState(STARTER_CODE);
  const [output, setOutput]     = useState([]);   // { text: string, err: boolean }[]
  const [stdin, setStdin]       = useState("");
  const [status, setStatus]     = useState("loading");
  const [execMs, setExecMs]     = useState(null);
  const [copied, setCopied]     = useState(false);

  const pyodideRef  = useRef(null);
  const outputEndRef = useRef(null);

  /* ── Load Pyodide once ── */
  useEffect(() => {
    // Re-use across hot reloads
    if (window.__kv_pyodide) {
      pyodideRef.current = window.__kv_pyodide;
      setStatus("ready");
      return;
    }

    const script = document.createElement("script");
    script.src   = `${PYODIDE_CDN}pyodide.js`;
    script.async = true;

    script.onload = async () => {
      try {
        const py = await window.loadPyodide({ indexURL: PYODIDE_CDN });
        pyodideRef.current    = py;
        window.__kv_pyodide   = py;
        setStatus("ready");
      } catch {
        setStatus("error");
      }
    };
    script.onerror = () => setStatus("error");
    document.head.appendChild(script);

    return () => {
      // Don't remove — Pyodide must persist across component re-mounts
    };
  }, []);

  /* ── Auto-scroll output ── */
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [output]);

  const push = useCallback((text, err = false) => {
    setOutput((prev) => [...prev, { text, err }]);
  }, []);

  /* ── Run code ── */
  const runCode = async () => {
    if (!pyodideRef.current || status !== "ready") return;
    const py = pyodideRef.current;

    setStatus("running");
    setOutput([]);
    setExecMs(null);

    // Redirect stdout / stderr to our output array
    py.setStdout({ batched: (t) => push(t, false) });
    py.setStderr({ batched: (t) => push(t, true)  });

    // Override input() to consume the stdin textarea (one value per line)
    const stdinLines = stdin ? stdin.split("\n") : [];
    py.globals.set("__kv_stdin__", py.toPy(stdinLines));
    try {
      await py.runPythonAsync(`
import builtins as _bi
_kv_iter = iter(__kv_stdin__)
def _kv_input(prompt=""):
    if prompt:
        print(prompt, end="", flush=True)
    return next(_kv_iter, "")
_bi.input = _kv_input
`);
    } catch { /* ignore — setup error, will surface in user code run */ }

    const t0 = performance.now();
    try {
      await py.runPythonAsync(code);
    } catch (e) {
      push(e.message, true);
    } finally {
      setExecMs((performance.now() - t0).toFixed(1));
      setStatus("ready");
    }
  };

  /* ── UI actions ── */
  const clearOutput = () => { setOutput([]); setExecMs(null); };
  const resetCode   = () => setCode(STARTER_CODE);
  const copyCode    = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Render ── */
  return (
    // height = full viewport minus the 60px pt wrapper in App.js
    <div
      className="flex flex-col bg-richblack-900"
      style={{ height: "calc(100vh - 60px)" }}
    >
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 bg-richblack-800 px-5 py-3">
        {/* Brand + status */}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-yellow-50/20 bg-yellow-50/10">
            <FiCode size={15} className="text-yellow-50" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold text-richblack-5">Python Playground</p>
            <p className="text-[10px] text-richblack-500">Pyodide · in-browser · no server</p>
          </div>
          <div className="mx-2 h-5 w-px bg-richblack-700" />
          <StatusPill status={status} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button onClick={copyCode} className={`${DS.btn.ghost} !py-1.5 !px-3 text-xs`}>
            {copied
              ? <><FiCheck size={12} className="text-caribbeangreen-100" /> Copied</>
              : <><FiCopy size={12} /> Copy</>}
          </button>
          <button onClick={resetCode} className={`${DS.btn.ghost} !py-1.5 !px-3 text-xs`}>
            <FiRefreshCw size={12} /> Reset
          </button>
          <button onClick={clearOutput} className={`${DS.btn.secondary} !py-1.5 !px-3 text-xs`}>
            <FiTrash2 size={12} /> Clear
          </button>
          <button
            onClick={runCode}
            disabled={status !== "ready"}
            className={`${DS.btn.primary} !py-1.5 !px-5 text-sm disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {status === "running"
              ? <><FiLoader size={14} className="animate-spin" /> Running…</>
              : <><FiPlay size={14} /> Run</>}
          </button>
        </div>
      </header>

      {/* ── Pane area ───────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden flex-col md:flex-row">

        {/* ── Editor pane ── */}
        <div className="flex flex-1 min-h-0 flex-col border-b border-richblack-700 md:border-b-0 md:border-r md:flex-none md:w-[58%]">
          {/* Tab bar */}
          <div className="flex flex-shrink-0 items-center gap-2 border-b border-richblack-700 bg-richblack-800/60 px-4 py-1.5">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F56]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
            </div>
            <span className="ml-2 text-[11px] text-richblack-400">main.py</span>
          </div>

          {/* Ace editor – fills remaining height */}
          <div className="flex-1 overflow-hidden">
            <AceEditor
              mode="python"
              theme="monokai"
              value={code}
              onChange={setCode}
              name="kv-python-editor"
              fontSize={14}
              showPrintMargin={false}
              showGutter={true}
              highlightActiveLine={true}
              width="100%"
              height="100%"
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 4,
                useSoftTabs: true,
                wrap: false,
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace",
              }}
            />
          </div>
        </div>

        {/* ── Right pane: output + stdin ── */}
        <div className="flex flex-1 min-h-0 flex-col md:w-[42%]">

          {/* Output */}
          <div className="flex flex-[3] flex-col overflow-hidden border-b border-richblack-700">
            <div className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 bg-richblack-800/60 px-4 py-1.5">
              <div className="flex items-center gap-2">
                <FiTerminal size={12} className="text-richblack-400" />
                <span className="text-[11px] text-richblack-400">Output</span>
              </div>
              {execMs !== null && (
                <span className="text-[10px] text-richblack-600">
                  Finished in {execMs} ms
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto bg-[#1e1e1e] p-4 font-mono text-sm leading-relaxed">
              {/* Loading hint */}
              {status === "loading" && (
                <p className="text-xs text-richblack-500">
                  ⏳ Downloading Python runtime (~10 MB). This happens only once per session.
                </p>
              )}
              {/* Load error */}
              {status === "error" && (
                <p className="text-xs text-pink-300">
                  ✗ Failed to load Pyodide. Check your network connection and refresh.
                </p>
              )}
              {/* Empty state */}
              {status === "ready" && output.length === 0 && (
                <p className="text-xs text-richblack-600">
                  Press <kbd className="rounded bg-richblack-700 px-1 py-0.5 text-richblack-300">Run</kbd> to execute your code.
                </p>
              )}
              {/* Output lines */}
              {output.map((line, i) => (
                <pre
                  key={i}
                  className={`whitespace-pre-wrap break-words ${
                    line.err ? "text-red-400" : "text-[#A9DC76]"
                  }`}
                >
                  {line.text}
                </pre>
              ))}
              <div ref={outputEndRef} />
            </div>
          </div>

          {/* Stdin */}
          <div className="flex flex-[1] flex-col" style={{ minHeight: 0 }}>
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-richblack-700 bg-richblack-800/60 px-4 py-1.5">
              <span className="text-[11px] text-richblack-400">Stdin</span>
              <span className="text-[10px] text-richblack-600">
                — values for input(), one per line
              </span>
            </div>
            <textarea
              value={stdin}
              onChange={(e) => setStdin(e.target.value)}
              placeholder={"value1\nvalue2\n..."}
              spellCheck={false}
              className="flex-1 resize-none bg-[#1e1e1e] p-4 font-mono text-sm text-richblack-200 placeholder:text-richblack-700 focus:outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PythonCompiler;
